import type { SearchHeadlinesParams } from "@/widget-components/newsquawk-headlines/api/types";
import type { SearchStoriesParams, BackendSource } from "@/shared/api/firehose";

/**
 * The same search, aimed at the third-party wire.
 *
 * Derived from the headline config rather than from the intent, so the two
 * halves cannot drift: whatever text and tickers the Newsquawk side ended up
 * searching for is exactly what this searches for.
 *
 * **Tags are dropped, and that is the real difference between the two.** The
 * firehose has no tag dimension, so "Fed speakers" resolving to a tag id
 * narrows the desk wire precisely while this side can only match the words.
 * The results are genuinely less precise, which is why they are shown as their
 * own section rather than interleaved as though the ranking were comparable.
 *
 * `sources` must be the ones the account can actually see — the caller passes
 * them from `useFirehoseSources`, which applies `isSourceAccessible`. Searching
 * the full source list would surface content the user is not entitled to.
 */
export function toStoriesParams(
  config: SearchHeadlinesParams,
  sources: BackendSource[]
): SearchStoriesParams {
  const tickers = (config.portfolios ?? []).flatMap((p) => p.tickers ?? []);

  return {
    q: config.query || null,
    limit: config.rows,
    source: sources,
    // Tickers reach the wire through a portfolio here too — searchStories
    // reads them off `portfolios`, exactly as transformSearchParams does.
    ...(tickers.length > 0 && { portfolios: config.portfolios }),
    date_range: config.dateRange?.startDate
      ? { start_date: config.dateRange.startDate, end_date: config.dateRange.endDate }
      : undefined,
  };
}
